import React from 'react';
import { css, Global } from '@emotion/react';
import type { CSSObject } from '@mui/system';
import { animated } from '@react-spring/web';
import styled from 'threads5/styles/styled';

import {
  Card,
  Slider,
  ToggleLabel,
  ToggleTrack,
} from '../Calculator/Calculator.styled';

/**
 * The P3 text style, as the `p3` variant in TypographyThemes defines it. There
 * is no web 3.0 P3 in the design system's typography.ts, so the values are
 * named here until there is one to read them from.
 */
const P3_FONT_SIZE = '1.6rem';
const P3_LINE_HEIGHT = '150%';

/**
 * The width of the card's metric toggle below `sm`. The shared toggle is 250px
 * there, which is too narrow for the longer label.
 *
 * The card's 40px side padding leaves the toggle only about 240px on a phone,
 * so it is allowed to run out into that padding. It is centred, and held at
 * least `MOBILE_TOGGLE_MIN_INSET` from each edge of the card so it can't
 * spill out of it on the narrowest screens.
 */
const MOBILE_TOGGLE_WIDTH = 290;
const MOBILE_TOGGLE_MIN_INSET = 12;
// The shared Card's side padding, which the toggle can borrow from.
const CARD_SIDE_PADDING = 40;

/**
 * The unselected option is dimmer than the `base/black/600` the ACH
 * calculator uses. The selected one keeps the shared colour.
 *
 * Below `sm` both options are set in P3 and held to one line, so the longer
 * label can't wrap inside the narrower toggle.
 */
export const MetricToggleLabel = styled(ToggleLabel, {
  shouldForwardProp: (prop) => {
    return prop !== 'isChecked';
  },
})<{ isChecked?: boolean }>(({ theme, isChecked = false }) => {
  return {
    ...(!isChecked && { color: theme.palette['text/contrast-low'] }),
    [theme.breakpoints.down('sm')]: {
      fontSize: P3_FONT_SIZE,
      lineHeight: P3_LINE_HEIGHT,
      whiteSpace: 'nowrap',
    },
  };
});

/**
 * The card's toggle over the elizabeth gradient rather than the flat grey the
 * ACH calculator uses. The stops run left to right in the order the design
 * names them.
 *
 * The knob is dragged from here rather than from the knob itself, because the
 * knob sits behind the labels so its text stays readable and so never receives
 * the pointer. Grabbing anywhere on the pill works as a result.
 */
export const MetricToggleTrack = styled(ToggleTrack, {
  shouldForwardProp: (prop) => {
    return prop !== 'isDragging';
  },
})<{ isDragging?: boolean }>(({ theme, isDragging = false }) => {
  return {
    background: `linear-gradient(90deg, ${[
      `${theme.palette['gradient/background/stark/elizabeth-start']} 0%`,
      `${theme.palette['gradient/background/stark/elizabeth-mid']} 50%`,
      `${theme.palette['gradient/background/stark/elizabeth-end']} 100%`,
    ].join(', ')})`,
    cursor: 'grab',
    // Otherwise a touch drag scrolls the page instead of moving the knob.
    touchAction: 'none',
    [theme.breakpoints.down('sm')]: {
      // The shared toggle's auto margins would pin an overflowing item to the
      // start edge, ahead of `align-self`.
      margin: 0,
      alignSelf: 'center',
      width: `${MOBILE_TOGGLE_WIDTH}px`,
      maxWidth: `calc(100% + ${
        2 * (CARD_SIDE_PADDING - MOBILE_TOGGLE_MIN_INSET)
      }px)`,
    },
    ...(isDragging && {
      // The labels set their own cursor, so they have to be told too. Holding
      // them inert is what stops the click that follows the release from
      // selecting whichever one the pointer happened to land on.
      '&, & label': { cursor: 'grabbing' },
      '& label': { pointerEvents: 'none' },
    }),
  };
});

/**
 * Wider than the 416px the ACH and Mortgage calculators share, because the
 * longest metric heading needs 339px to stay on one line and the shared card
 * only offers 336px between its 40px gutters. The width is pinned rather than
 * left to the content so the extra room survives copy changes. The shared
 * card's own small-screen override has to be repeated, since this rule is
 * emitted after it.
 */
export const ResultCard = styled(Card)(({ theme }) => {
  return {
    width: '100%',
    maxWidth: '448px',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  };
});

/**
 * The angular gradient the knob ellipse is filled with. The stops fall
 * outside 0-360deg and the colours are one-off values with no palette
 * equivalents, so neither can be tidied without redrawing the sweep.
 */
const TOGGLE_KNOB_GRADIENT = `conic-gradient(
  from 180deg at 50% 50%,
  #ff8741 -24.2deg,
  #efefef 30.6deg,
  #ffd900 64.8deg,
  rgba(225, 0, 255, 0.05) 124.2deg,
  #ffd900 196.2deg,
  #ff8741 259.2deg,
  rgba(225, 0, 255, 0.09) 312.5deg,
  #ff8741 335.8deg,
  #efefef 390.6deg
)`;

/**
 * The gradient-filled ellipse behind the knob. It is far larger than the
 * knob, so the knob clips it and shows a small window onto the sweep.
 *
 * The offsets are not the design's: those give the bounding box of the
 * rotated ellipse, whereas CSS positions the un-rotated box and spins it
 * about its centre. Converting lands it 84px right and 19px down from the
 * knob's top left.
 */
const KNOB_ELLIPSE: CSSObject = {
  content: '""',
  position: 'absolute',
  top: '-226px',
  left: '-111px',
  width: '390px',
  height: '490px',
  borderRadius: '50%',
  backgroundImage: TOGGLE_KNOB_GRADIENT,
  pointerEvents: 'none',
};

/**
 * The knob that slides behind the card's toggle labels. Its transform is left
 * to the spring the card drives, matching the ACH calculator's knob.
 */
export const ToggleKnob = styled(animated.div)(({ theme }) => {
  return {
    position: 'absolute',
    top: '3px',
    left: '3px',
    width: 'calc(50% - 3px)',
    height: '48px',
    borderRadius: '40px',
    backgroundColor: theme.palette['base/white'],
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.20)',
    // Clips both ellipses to the pill. The knob's own transform also isolates
    // their blending, so they mix with the white fill rather than the track.
    overflow: 'hidden',
    // Excluding the warm sweep against the white fill inverts it into the
    // silvery cast the design shows. The angle is negated because Figma
    // measures rotation anticlockwise and CSS clockwise.
    //
    // The design stacks a second, lightened ellipse under this one. It is
    // left out because lighten takes the per-channel maximum against its
    // backdrop, and against an opaque white fill that is always white; the
    // only pixels it reached were the pill's antialiased rim, which it gave
    // a warm fringe.
    '&::before': {
      ...KNOB_ELLIPSE,
      transform: 'rotate(120.97deg)',
      opacity: 0.15,
      mixBlendMode: 'exclusion',
    },
  };
});

/**
 * The stops of the rate sliders' track gradient, carried in custom properties
 * so activating a slider can fade between the gradient and the flat rail.
 * `background-image` is not interpolable, so transitioning the gradient itself
 * only ever swaps it; moving the colours out into properties transitions them
 * instead and the gradient follows.
 *
 * The offsets are the shared slider's own, so the active track matches the ACH
 * and Mortgage calculators exactly.
 */
const RATE_TRACK_STOPS = [
  { color: '#42F0CD', name: '--lend-score-rate-track-1', offset: '0%' },
  { color: '#56DDF7', name: '--lend-score-rate-track-2', offset: '33.65%' },
  { color: '#D8C2FB', name: '--lend-score-rate-track-3', offset: '65.38%' },
  { color: '#E9F', name: '--lend-score-rate-track-4', offset: '100%' },
];

const RATE_TRACK_GRADIENT = `linear-gradient(90deg, ${RATE_TRACK_STOPS.map(
  (stop) => {
    return `var(${stop.name}) ${stop.offset}`;
  },
).join(', ')})`;

/**
 * Registers the stops. An unregistered custom property has no type, so the
 * browser can only swap its value; registering these as colours is what makes
 * them animate. They inherit because the gradient is painted by the track
 * pseudo-element, which has to pick the values up from the input.
 *
 * `@property` is only valid at the top level of a stylesheet, so it cannot
 * live in the styled rule that uses it.
 */
export const RateTrackStopProperties = () => {
  return (
    <Global
      styles={css`
        ${RATE_TRACK_STOPS.map((stop) => {
          return `@property ${stop.name} {
            syntax: '<color>';
            inherits: true;
            initial-value: ${stop.color};
          }`;
        }).join('\n')}
      `}
    />
  );
};

// The shared slider's own track height, which the mobile touch area below is
// built around.
const SLIDER_TRACK_HEIGHT = 10;

// The smallest area a finger can be expected to hit reliably.
const TOUCH_TARGET = 44;

/**
 * The rate sliders extend the slider the ACH and Mortgage calculators share so
 * the track, thumb and hover behaviour stay identical across all three. Only
 * two things differ here, and both are overridden below: the shared slider is
 * hidden below `sm` because those calculators pair every slider with a text
 * input, and it has no inactive state.
 */
export const RateSlider = styled(Slider, {
  shouldForwardProp: (prop) => {
    return prop !== 'isActive';
  },
})<{ isActive?: boolean }>(({ theme, isActive = false }) => {
  const resting =
    theme.palette['graphic/interactive/neutral/resting/contrast-low'];

  return {
    // This calculator has no text input beside the slider, so unlike ACH and
    // Mortgage it has to stay usable on small screens.
    display: 'block',
    cursor: 'pointer',

    // Matches the thumb's own transitions on the shared slider, so the whole
    // control settles together. The stops are transitioned here rather than on
    // the track, because this is the element their value changes on.
    transition: RATE_TRACK_STOPS.map((stop) => {
      return `${stop.name} 300ms ease-in-out`;
    }).join(', '),

    // The unfilled remainder of the track uses the neutral resting colour
    // rather than the ACH calculator's own track token.
    '&::after': {
      background: resting,
    },

    // The inactive slider takes every stop to the resting colour, so the whole
    // track reads as one flat rail and meets the unfilled remainder without a
    // seam at the thumb.
    ...(isActive
      ? {}
      : Object.fromEntries(
          RATE_TRACK_STOPS.map((stop) => {
            return [stop.name, resting];
          }),
        )),

    '::-webkit-slider-runnable-track': { background: RATE_TRACK_GRADIENT },
    '::-moz-range-track': { background: RATE_TRACK_GRADIENT },
    '::-moz-range-progress': { background: RATE_TRACK_GRADIENT },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette['graphic/contrast-high']}`,
      outlineOffset: '6px',
    },

    // Below `sm` the slider gets a 44px touch area, and the thumb with it: a
    // touch anywhere in the box picks the thumb up and moves it, so the thumb
    // is reachable across the whole height. The thumb itself is left at its
    // usual size on purpose. A wider one shortens how far it can travel, which
    // pulls it out of step with the unfilled remainder of the track.
    //
    // The extra height is only there to be touched, so the negative margin
    // takes it back out of the layout and the field keeps the spacing it had.
    // The unfilled remainder is pinned to the middle of the taller box, where
    // the track now sits.
    //
    // The taller box is also left clear, so it can't show up as a white band
    // behind the track.
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
      height: TOUCH_TARGET,
      margin: `${(SLIDER_TRACK_HEIGHT - TOUCH_TARGET) / 2}px 0`,
      '&::after': {
        top: `${(TOUCH_TARGET - SLIDER_TRACK_HEIGHT) / 2}px`,
      },
    },

    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  };
});
